import React, { useCallback, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { BannerSource } from '@/store/useBannerStore/useBannerStore.types';
import { AdriverBanner } from '@/components/Banners/AdriverBanner';
import {
  ADRIVER_SID,
  AdriverBannerId,
} from '@/components/Banners/Banners.constants';
import { BannerName } from '@/components/Banners/Banners.types';
import { AdManagerBanner } from '@/components/Banners/AdManagerBanner';
import { TargetParams } from '@/components/Banners/adriver';

export function useBanner({
  targetParams,
  isDesktop = false,
  canLoadTopBanner = true,
}: {
  isDesktop?: boolean;
  targetParams?: TargetParams;
  canLoadTopBanner?: boolean;
} = {}) {
  const pathname = usePathname();
  const { isWebView } = useBridgeState();
  const [
    bannerTopSource,
    bannerBottomSource,
    bannerBackgroundSource,
    resetBannersState,
    isBannersReloading,
  ] = useBannerStore(state => [
    state.bannerTopSource,
    state.bannerBottomSource,
    state.bannerBackgroundSource,
    state.resetBannersState,
    state.isBannersReloading,
  ]);

  useEffect(() => {
    return () => {
      if (!isWebView) {
        resetBannersState();
      }
    };
  }, [pathname, isWebView]);

  const topBanner = useMemo(() => {
    if (!canLoadTopBanner || isWebView || isBannersReloading) return null;

    const containerId = isDesktop
      ? AdriverBannerId[BannerName.TopDesktop]
      : AdriverBannerId[BannerName.Top];

    return bannerTopSource === BannerSource.Adriver ? (
      <AdriverBanner
        tagName="div"
        containerId={containerId}
        containerClassName="lotriver-top-banner"
        sid={ADRIVER_SID}
        arCn={1}
        bn={isDesktop ? '5' : '2'}
        targetParams={targetParams}
      />
    ) : (
      <AdManagerBanner
        bannerName={isDesktop ? BannerName.TopDesktop : BannerName.Top}
      />
    );
  }, [
    pathname,
    bannerTopSource,
    targetParams,
    canLoadTopBanner,
    isDesktop,
    isWebView,
    isBannersReloading,
  ]);

  const bottomBanner = useMemo(() => {
    if (isWebView || isBannersReloading) return null;

    return bannerBottomSource === BannerSource.Adriver ? (
      <AdriverBanner
        tagName="p"
        containerId={AdriverBannerId[BannerName.Bottom]}
        containerClassName="lotriver-bottom-banner"
        sid={ADRIVER_SID}
        bn="4"
        arCn={3}
      />
    ) : (
      <AdManagerBanner bannerName={BannerName.Bottom} />
    );
  }, [pathname, bannerBottomSource, isWebView, isBannersReloading]);

  const backgroundBanner = useMemo(() => {
    if (!isDesktop || isWebView || isBannersReloading) return null;

    return bannerBackgroundSource === BannerSource.Adriver ? (
      <AdriverBanner
        tagName="div"
        containerId={AdriverBannerId[BannerName.Background]}
        sid={ADRIVER_SID}
        bt="52"
        bn="3"
        targetParams={targetParams}
        isAjax
      />
    ) : (
      <>
        <AdManagerBanner bannerName={BannerName.BackgroundLeft} />
        <AdManagerBanner bannerName={BannerName.BackgroundRight} />
      </>
    );
  }, [
    pathname,
    bannerBackgroundSource,
    isDesktop,
    isBannersReloading,
    targetParams,
  ]);

  const inListBanner = useCallback(
    (arCn: number) => {
      if (isWebView || isBannersReloading) return null;

      return (
        <AdriverBanner
          tagName="div"
          containerId={`js-lotriver-bottom-banner3-${arCn}`}
          containerClassName="lotriver-bottom-banner lotriver-bottom-banner--in-list"
          sid={ADRIVER_SID}
          bn="4"
          arCn={arCn}
        />
      );
    },
    [isWebView, isBannersReloading]
  );

  const inListDesktopBanner = useMemo(() => {
    if (isWebView || isBannersReloading) return null;

    return (
      <AdriverBanner
        tagName="div"
        containerId={AdriverBannerId[BannerName.InListDesktop]}
        sid={ADRIVER_SID}
        arCn={2}
        bn="14"
      />
    );
  }, [isWebView, isBannersReloading]);

  return {
    topBanner,
    bottomBanner,
    inListBanner,
    inListDesktopBanner,
    backgroundBanner,
  };
}
